import { useState } from 'react';
import { IonButton, IonContent, IonPage, IonSpinner } from '@ionic/react';
import { Trans, useTranslation } from 'react-i18next';
import { nuclearReset } from '../services/appResetService';
import { isAndroid, isIOS, isStandalonePWA } from '../services/platformService';
import { logError } from '../services/loggerService';

/**
 * Terminal page surfaced when the /ping lifeline detected we're below the
 * BE's minimum-supported FE version AND an automatic nuclear reset has
 * already been attempted this session (loop-guard fired).
 *
 * Two ways out:
 *   - "Apply Update" button → runs `nuclearReset()` again. Useful if the
 *     first attempt was thwarted by a transient network issue and the
 *     user is trying again.
 *   - Manual reinstall instructions (per platform) as a last-resort
 *     fallback when even repeated automatic resets don't unstick the user.
 *
 * Exempt from all app gates (RegistrationGate, ProtectedRoute) — see App.tsx.
 * No other navigation matters from here.
 */

type Surface = 'iosPwa' | 'androidChromePwa' | 'desktopPwa' | 'browser';

function detectSurface(): Surface {
  const standalone = isStandalonePWA();
  if (isIOS() && standalone) return 'iosPwa';
  if (isAndroid() && standalone) return 'androidChromePwa';
  if (standalone) return 'desktopPwa';
  return 'browser';
}

export const UpdateRequiredPage: React.FC = () => {
  const { t } = useTranslation('common');
  const surface = detectSurface();
  const [applying, setApplying] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  // returnObjects: true is needed for array values — fallbackSteps.<surface>
  // is a string[] in the locale bundle (the steps render as a numbered list).
  const fallbackSteps = t(`pages.updateRequired.fallbackSteps.${surface}`, {
    returnObjects: true,
  }) as string[];

  const handleApply = async () => {
    if (applying) return;
    setApplying(true);
    try {
      await nuclearReset();
    } catch (err) {
      logError(
        err instanceof Error ? err : new Error(String(err)),
        'UpdateRequiredPage: nuclearReset threw',
      );
      // If even the reset throws, fall back to a plain reload — better
      // than leaving the user staring at a frozen button.
      window.location.reload();
    }
  };

  return (
    <IonPage>
      <IonContent>
        <div className="mx-auto max-w-md px-4 py-12 flex flex-col items-center gap-6 text-center">
          <h1 className="text-2xl font-bold text-foreground">{t('pages.updateRequired.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {/* Trans renders the <strong> tag inline; the matching <0> placeholder
                lives in the translated body string in common.json. */}
            <Trans
              t={t}
              i18nKey="pages.updateRequired.body"
              components={{ strong: <strong /> }}
            />
          </p>

          <IonButton
            expand="block"
            onClick={handleApply}
            disabled={applying}
            className="w-full"
          >
            {applying ? (
              <span className="flex items-center gap-2">
                <IonSpinner name="crescent" />
                {t('pages.updateRequired.applying')}
              </span>
            ) : (
              t('pages.updateRequired.applyCta')
            )}
          </IonButton>

          <div className="w-full pt-4 border-t border-border">
            <button
              type="button"
              onClick={() => setShowFallback((v) => !v)}
              className="text-sm text-muted-foreground underline"
            >
              {showFallback
                ? t('pages.updateRequired.hideInstructions')
                : t('pages.updateRequired.fallbackHeading')}
            </button>

            {showFallback && (
              <ol className="mt-3 text-left text-sm text-muted-foreground list-decimal list-inside space-y-2">
                {fallbackSteps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};
